import {
	DataGridPro,
	GridColDef,
	GridFilterModel,
	GridRowClassNameParams,
	GridRowParams,
	GridToolbarProps,
	GridValidRowModel,
	Toolbar,
	ToolbarPropsOverrides
} from '@mui/x-data-grid-pro';
import { ReactNode, useId, useMemo, useState } from 'react';
import { Theme, useMediaQuery } from '@mui/material';

import styles from '../RenderCell.module.css';
import { StyledQuickFilter } from '../StyledComponents';

interface FilterableDataGridProps<T extends GridValidRowModel> {
	columnDefinitions: GridColDef<T>[];
	onFilterCallback: (searchValue: string) => Promise<T[]>;
	isRowSelectable?: (row: T) => boolean;
	onRowSelect?: (params: GridRowParams<T>) => void;
	getRowClassName?: (row: T) => string | undefined;
	toolbarContent?: ReactNode;
	initialRows?: T[];
}

/**
 * Generic Data Grid that is populated based on the Quick Filter input.
 */
export default function FilterableDataGrid<T extends GridValidRowModel>({ onFilterCallback, ...props }: FilterableDataGridProps<T>) {
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
	const [rows, setRows] = useState<T[]>(props.initialRows ?? []);

	const onFilterChange = (model: GridFilterModel) => {
		const searchString = model.quickFilterValues?.join(' ');

		if (searchString) {
			onFilterCallback(searchString)
				.then((response) => {
					setRows(response);
				})
				.catch((error: unknown) => {
					console.log('error', error);
				});
		} else {
			setRows([]);
		}
	};

	const getRowSelectability = (params: GridRowParams<T>) => {
		if (props.isRowSelectable) return props.isRowSelectable(params.row);
		return true;
	};

	const onRowClick = (params: GridRowParams<T>) => {
		/** If we received a on select callback, proceed */
		if (props.onRowSelect) {
			/** If we recieved row validation, ensure row is selectable else return */
			if (props.isRowSelectable && !props.isRowSelectable(params.row)) return;

			props.onRowSelect(params);
		}
	};

	const getRowStyles = (params: GridRowClassNameParams<T>) => {
		const customRowClasses = props.getRowClassName ? props.getRowClassName(params.row) : undefined;

		let rowStyles = `${customRowClasses ?? ''} ${styles['row']}`.trimStart();

		if (props.isRowSelectable && !props.isRowSelectable(params.row)) {
			rowStyles = `${rowStyles} ${styles['disabled-row']}`.trimStart();
		}

		return rowStyles;
	};

	const columnHeaderHeight = useMemo(() => (isMobile ? 0 : 50), [isMobile]);

	return (
		<DataGridPro
			columns={props.columnDefinitions}
			rows={rows}
			isRowSelectable={getRowSelectability}
			columnHeaderHeight={columnHeaderHeight}
			getRowClassName={getRowStyles}
			onRowClick={onRowClick}
			onFilterModelChange={onFilterChange}
			slots={{
				toolbar: FilterableDataGridToolbar
			}}
			slotProps={{
				toolbar: {
					filterableDataGridToolbar: {
						toolbarContent: props.toolbarContent
					}
				}
			}}
			rowHeight={50}
			className={styles['custom-data-grid']}
		/>
	);
}

const FilterableDataGridToolbar = (props: GridToolbarProps & ToolbarPropsOverrides) => {
	const generatedId = useId();
	/**
	 * In Mobile resolutions the Auto Focus flag needs to be turned off to resolve a bug where the focus causes
	 * the mobile device keyboard to shoot out at the same time the MUI Slide Transition is sliding up resulting in
	 * our component mounting out of the screen area
	 */
	const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

	return (
		<Toolbar className={styles['filterable-data-grid-toolbar']}>
			{props.filterableDataGridToolbar?.toolbarContent && <div>{props.filterableDataGridToolbar.toolbarContent}</div>}
			<StyledQuickFilter
				id={generatedId}
				className={styles['quick-filter']}
				defaultExpanded
				autoFocus={!isMobile}
			/>
		</Toolbar>
	);
};
